// Pricing presets for the assignment form's Work Types & Work Quantity table.
//
// A preset is the fixed set of work types an assignment of a given project +
// request type is normally billed on. Picking one fills the table with exactly
// those rows and locks everything except the quantities. "Custom" is the escape
// hatch: rows can be added, retyped and removed freely there and nowhere else.
//
// The preset itself is NOT stored on the assignment — the API has no field for
// it (parseAssignmentResponse hardcodes pricingModel to null and the serializer
// never sends it) — so it is recovered on load by matching the saved rows back
// against these definitions. That recovery is isolated in inferPricingPreset
// below: if a persisted field is added later, that one function is what changes.

// The work types each preset is built from. These are fixed codes, not a
// lookup: an FC localization assignment bills per word on LOC_09-02-16 and per
// unit on LOC_300-100-700 whatever the language pair.
export const PER_WORD_WORK_TYPE = "LOC_09-02-16";
export const PER_UNIT_WORK_TYPE = "LOC_300-100-700";

// The six copywriting tiers, in the order the design lists them.
export const FC_COPYWRITING_WORK_TYPES = [
  "CWR_500-60-1100",
  "CWR_1000-120-1500",
  "CWR_1500-180-2800",
  "CWR_2500-300-3800",
  "CWR_5000-500-7000",
  "CWR_300-100-500",
];

export const CUSTOM_PRESET = "Custom";

const FC_LOC_PER_WORD = "FC Localization: Per Word";
const FC_LOC_PER_UNIT = "FC Localization: Per Unit";
const FC_LOC_COMBINED = "FC Localization Combined (Per Word + Per Unit)";
const FC_COPYWRITING = "FC Copywriting";

const ITUNES_FC = "iTunes FC";

// `workTypeNames` returns the rows the preset is made of, or null when the
// preset does not prescribe rows at all (Custom).
const PRESETS = {
  [FC_LOC_PER_WORD]: {
    quantityLabel: "Quantity (Words)",
    workTypeNames: [PER_WORD_WORK_TYPE],
  },
  [FC_LOC_PER_UNIT]: {
    quantityLabel: "Quantity (Units)",
    workTypeNames: [PER_UNIT_WORK_TYPE],
  },
  [FC_LOC_COMBINED]: {
    quantityLabel: "Quantity (Words, Units)",
    workTypeNames: [PER_WORD_WORK_TYPE, PER_UNIT_WORK_TYPE],
  },
  [FC_COPYWRITING]: {
    quantityLabel: "Quantity",
    workTypeNames: FC_COPYWRITING_WORK_TYPES,
  },
  [CUSTOM_PRESET]: {
    quantityLabel: "Quantity",
    workTypeNames: null,
  },
};

// Custom is anything that isn't one of the four fixed presets — which also
// quietly absorbs "Non FC", the label this dropdown used before presets existed.
export const isCustomPricingPreset = (preset) =>
  !preset || preset === CUSTOM_PRESET || !PRESETS[preset] || preset === "Non FC";

// Which presets this project + request type may use, most appropriate first —
// the first entry is the default. Custom is always available and always last.
export const pricingPresetOptions = (form) => {
  const isITunesFC = form?.relatedProject?.name === ITUNES_FC;

  if (isITunesFC && form?.requestType === "Localization") {
    return [FC_LOC_PER_WORD, FC_LOC_PER_UNIT, FC_LOC_COMBINED, CUSTOM_PRESET];
  }

  if (isITunesFC && form?.requestType === "Copywriting") {
    return [FC_COPYWRITING, CUSTOM_PRESET];
  }

  return [CUSTOM_PRESET];
};

export const defaultPricingPreset = (form) => pricingPresetOptions(form)[0];

export const pricingPresetQuantityLabel = (preset) =>
  PRESETS[preset]?.quantityLabel ?? PRESETS[CUSTOM_PRESET].quantityLabel;

// The work type names a preset prescribes, or null for Custom. Callers treat
// null as "leave the rows alone".
export const pricingPresetWorkTypeNames = (preset) =>
  PRESETS[preset]?.workTypeNames ?? null;

const sameNameSet = (a, b) => {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((name, i) => name === right[i]);
};

// Which preset a loaded assignment was built with, worked out from its rows,
// because the API does not store the choice. Returns null when there are no rows
// to judge by, so the caller can fall back to the default for the context.
//
// Only presets valid for this project + request type are considered: a lone
// LOC row on some other client's project is a custom row, not FC Per Word.
export const inferPricingPreset = (form) => {
  const names = (form?.pricing?.workTypes ?? [])
    .map((row) => row?.customerPriceList?.workTypeName)
    .filter(Boolean);
  if (!names.length) return null;

  const match = pricingPresetOptions(form).find((preset) => {
    const expected = pricingPresetWorkTypeNames(preset);
    return expected ? sameNameSet(expected, names) : false;
  });

  return match ?? CUSTOM_PRESET;
};

// Build a preset's rows against the live price list, carrying over the quantity
// already entered for any work type the new preset still contains — switching
// Per Word -> Combined should not make you retype the word count.
export const buildPricingPresetRows = (preset, form, priceList) => {
  const names = pricingPresetWorkTypeNames(preset);
  if (!names) return null;

  const quantityByName = new Map(
    (form?.pricing?.workTypes ?? [])
      .filter((row) => row?.customerPriceList?.workTypeName)
      .map((row) => [row.customerPriceList.workTypeName, row.quantity])
  );

  return names.map((name, index) => ({
    orderPosition: index + 1,
    customerPriceList:
      (priceList ?? []).find((entry) => entry.workTypeName === name) ?? null,
    quantity: quantityByName.has(name) ? quantityByName.get(name) : null,
  }));
};
